import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from '@aws-amplify/core';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  'https://bp7u0bhrc8.execute-api.eu-west-2.amazonaws.com/dev';

// TTL in ms (default 60s)
const DEFAULT_TTL = (process.env.REACT_APP_SUBSCRIPTION_CACHE_TTL_MS && Number(process.env.REACT_APP_SUBSCRIPTION_CACHE_TTL_MS)) || 60_000;

/* tiny http helper (same semantics as other code in repo) */
async function http(url, init = {}) {
  const res = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

/* helper to decode JWT payload (browser) */
function decodeJwtPayload(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

/* cache helpers (namespaced by userSub) */
function readCacheForKey(key) {
  try {
    const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.cachedAt || !parsed.data) return null;
    return parsed;
  } catch {
    return null;
  }
}
function writeCacheForKey(key, data, ttl) {
  try {
    const payload = JSON.stringify({ data, cachedAt: Date.now(), ttl: ttl || DEFAULT_TTL });
    // prefer sessionStorage (cleared on tab close); fallback to localStorage
    try {
      sessionStorage.setItem(key, payload);
    } catch {
      localStorage.setItem(key, payload);
    }
  } catch {}
}
function clearCacheForKey(key) {
  try { sessionStorage.removeItem(key); } catch {}
  try { localStorage.removeItem(key); } catch {}
}

/* remove all cm_subscription_cache_v2_* entries (used on sign-out) */
function clearAllSubscriptionCache() {
  try {
    const prefix = 'cm_subscription_cache_v2_';
    // sessionStorage keys
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(prefix)) sessionStorage.removeItem(k);
      }
    } catch {}
    // localStorage keys
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) localStorage.removeItem(k);
      }
    } catch {}
  } catch {}
  // also remove any stored stripe customer id used by UI
  try { localStorage.removeItem('stripeCustomerId'); } catch {}
}

/**
 * useCachedSubscription - improved UX-focused subscription cache
 * returns: { subscription, loading, refresh, clearCache, isCached, userSub }
 */
export default function useCachedSubscription({ forceRefresh = false, ttl = DEFAULT_TTL } = {}) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userSub, setUserSub] = useState(null);
  const cacheKeyRef = useRef(null);
  const ttlRef = useRef(ttl);

  const clearCache = useCallback(() => {
    const k = cacheKeyRef.current;
    if (k) clearCacheForKey(k);
    setSubscription(null);
    setUserSub(null);
    // also clear any global cached copies to be safe
    try { localStorage.removeItem('stripeCustomerId'); } catch {}
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken?.toString();
      if (!idToken) {
        // not signed in: clear any cache and return null
        clearAllSubscriptionCache();
        setLoading(false);
        return null;
      }

      const claims = decodeJwtPayload(idToken);
      const sub = claims?.sub;
      if (!sub) {
        clearAllSubscriptionCache();
        setLoading(false);
        return null;
      }

      const key = `cm_subscription_cache_v2_${sub}`;
      cacheKeyRef.current = key;
      setUserSub(sub);

      // Fetch from network
      const data = await http(`${API_BASE}/me/subscription`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      setSubscription(data);
      writeCacheForKey(key, data, ttlRef.current);
      return data;
    } catch (err) {
      console.debug('useCachedSubscription refresh failed:', err.message || err);
      // keep existing subscription if present; otherwise null
      return null;
    } finally {
      setLoading(false);
    }
  }, [clearCache]);

  /* init: try to load cache scoped to current user; otherwise refresh */
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      setLoading(true);
      try {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken?.toString();
        if (!idToken) {
          // not signed in
          clearAllSubscriptionCache();
          if (mounted) setLoading(false);
          return;
        }

        const claims = decodeJwtPayload(idToken);
        const sub = claims?.sub;
        if (!sub) {
          clearAllSubscriptionCache();
          if (mounted) setLoading(false);
          return;
        }

        const key = `cm_subscription_cache_v2_${sub}`;
        cacheKeyRef.current = key;
        setUserSub(sub);

        const cached = readCacheForKey(key);
        if (cached && cached.data) {
          const age = Date.now() - cached.cachedAt;
          if (age <= (cached.ttl || ttlRef.current) && !forceRefresh) {
            if (mounted) {
              setSubscription(cached.data);
              setLoading(false);
            }
            // schedule background refresh at half TTL
            const timer = setTimeout(() => { refresh(); }, Math.max(5_000, (cached.ttl || ttlRef.current) / 2));
            return () => clearTimeout(timer);
          }
        }

        // no valid cache -> fetch
        await refresh();
      } catch (err) {
        console.debug('useCachedSubscription init failed:', err.message || err);
        if (mounted) setSubscription(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();
    return () => { mounted = false; };
  }, [forceRefresh, refresh]);

  /* Listen to Amplify Auth events: signIn -> refresh, signOut -> clear cache */
  useEffect(() => {
    const listener = (data) => {
      const { payload } = data;
      const event = payload && payload.event;
      if (event === 'signOut' || event === 'cognitoHostedUI_signOut') {
        // clear all cached subscription data and UI customer id on sign out
        clearAllSubscriptionCache();
        clearCache();
      } else if (event === 'signIn' || event === 'cognitoHostedUI') {
        // refresh on sign in
        refresh();
      } else if (event === 'tokenRefresh') {
        // token refreshed: re-evaluate subscription in background
        refresh();
      }
    };

    // Hub.listen may return an unsubscribe function in some Amplify versions
    const unsubscribe = Hub.listen ? Hub.listen('auth', listener) : null;

    return () => {
      // try unsubscribe function first (modern Amplify), otherwise fall back to Hub.remove if present
      try {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        } else if (Hub && typeof Hub.remove === 'function') {
          Hub.remove('auth', listener);
        }
      } catch (e) {
        // ignore cleanup errors
        console.debug('Hub cleanup failed', e);
      }
    };
  }, [refresh, clearCache]);

  /* Refresh on window focus if cache is stale (improves tab-resume UX) */
  useEffect(() => {
    const onFocus = () => {
      const k = cacheKeyRef.current;
      if (!k) {
        // no signed-in user known, attempt refresh (refresh will clear if not signed in)
        refresh();
        return;
      }
      const cached = readCacheForKey(k);
      if (!cached) {
        refresh();
        return;
      }
      const age = Date.now() - cached.cachedAt;
      if (age > Math.max(5_000, (cached.ttl || ttlRef.current) / 2)) {
        // refresh if older than half TTL (or 5s)
        refresh();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onFocus();
    });
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  return {
    subscription,
    loading,
    refresh,
    clearCache,
    isCached: Boolean(subscription),
    userSub,
  };
}